import { useEffect, useState } from 'react'
import { deadlineFetch } from './customEndpointFetch'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'

/**
 * Per-source counts the backend returns so the UI can explain WHY a pet shows
 * up under a clinic ("linked via vet_share, not directly owned here").
 */
export interface ClinicLinkedPetsBreakdown {
  directOwner: number
  ownerVets: number
  allPetsShare: number
  specificShare: number
}

export interface ClinicLinkedPetsResult {
  /** The canonical set of pet IDs this clinic has any link to. null while loading or unavailable. */
  petIds: Set<number> | null
  breakdown: ClinicLinkedPetsBreakdown | null
  total: number | null
  isLoading: boolean
  error: string | null
}

/**
 * Fetches the canonical pet-id set for a vet clinic from /users/clinic-linked-pets.
 *
 * Why a dedicated hook rather than just useGetList: this endpoint is the
 * single source of truth shared with the Clinic Directory's pet_count.
 * If the list page filters pets by a different rule (the original bug —
 * client-side petMatchesVetClinic was incomplete), the count and the list
 * silently diverge. Going through this hook keeps them locked together.
 *
 * When vetId is null/undefined (no clinic filter active), the hook stays
 * dormant and returns null/null/0 — callers should branch on petIds === null
 * meaning "no filter, show everything" vs. an empty Set meaning "filter
 * active, this clinic has no linked pets."
 */
export const useClinicLinkedPets = (vetId: number | null): ClinicLinkedPetsResult => {
  const [petIds, setPetIds] = useState<Set<number> | null>(null)
  const [breakdown, setBreakdown] = useState<ClinicLinkedPetsBreakdown | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (vetId === null || vetId === undefined) {
      setPetIds(null)
      setBreakdown(null)
      setTotal(null)
      setError(null)
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    let cancelled = false
    setIsLoading(true)
    setError(null)

    // Auth via cookie OR token — NOT token-only. On a browser reopen / new tab
    // sessionStorage is empty but the HttpOnly payload-token cookie is still
    // valid, so the same-origin request authenticates via the cookie; attach the
    // JWT header only when a token happens to be present. Bailing on a missing
    // token used to show a FALSE "Not signed in." on every reopen even though
    // the user IS signed in via the cookie (L-019, sibling of a995249f).
    const token = sessionStorage.getItem('token')
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    // JWT scheme, not Bearer — same recurring convention as the other
    // custom-endpoint callers in this codebase.
    if (token) headers.Authorization = `JWT ${token}`

    const url = new URL(`${apiUrl}/users/clinic-linked-pets`, window.location.origin)
    url.searchParams.set('vetId', String(vetId))

    deadlineFetch(url.toString().replace(window.location.origin, ''), {
      headers,
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (resp) => {
        if (cancelled) return
        if (!resp.ok) {
          let detail = ''
          try {
            const body = await resp.json()
            detail = body?.error || body?.detail || ''
          } catch {
            /* ignore */
          }
          throw new Error(
            `Failed to load linked pets${detail ? ` — ${detail}` : ` (HTTP ${resp.status})`}`,
          )
        }
        const data = await resp.json()
        if (cancelled) return
        const ids = Array.isArray(data.petIds)
          ? data.petIds.filter((n: unknown) => typeof n === 'number')
          : []
        setPetIds(new Set(ids))
        setBreakdown(
          data.breakdown && typeof data.breakdown === 'object'
            ? {
                directOwner: Number(data.breakdown.directOwner) || 0,
                ownerVets: Number(data.breakdown.ownerVets) || 0,
                allPetsShare: Number(data.breakdown.allPetsShare) || 0,
                specificShare: Number(data.breakdown.specificShare) || 0,
              }
            : null,
        )
        setTotal(typeof data.total === 'number' ? data.total : ids.length)
      })
      .catch((e: any) => {
        if (cancelled || e?.name === 'AbortError') return
        // a message from the server, or a KEY the UI translates (translate(error, { _: error }))
        setError(e?.message || 'resources.pets.clinic_filter.load_failed')
        // Keep petIds=null on error so callers degrade to "show all" rather
        // than "show nothing" — matches useDevicesWithMetrics's fallback.
        setPetIds(null)
        setBreakdown(null)
        setTotal(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [vetId])

  return { petIds, breakdown, total, isLoading, error }
}
